<template>
  <Grid class="kpb-contact-list" :columns="columns" :gap="gap" :data-kpb-variant="variant">
    <div
      class="kpb-contact-list__item"
      v-for="(contact, index) in data.contacts"
      :key="index"
      :data-kpb-id="contact.id"
      :data-kpb-email="contact.email"
      :style="`--kpb-contact-list-aspect-ratio: ${aspectRatio};`"
    >
      <img class="kpb-contact-list__avatar" :src="contact.avatar" alt="" />
      <div class="kpb-contact-list__content">
        <h3 class="kpb-contact-list__name">{{ contact.name }}</h3>
        <p v-if="contact.title" class="kpb-contact-list__user-title">{{ contact.title }}</p>
        <span v-if="showCompany(contact)" class="kpb-contact-list__company-name">
          {{ contact.company.name }}
        </span>
        <div class="kpb-contact-list__contact-info">
          <a v-if="contact.phone" class="kpb-contact-list__phone" :href="`tel:${contact.phone}`">
            <template v-if="contactDetailStyle === 'icon'">
              <svg class="kpb-contact-list__contact-info-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path
                  d="M164.9 24.6c-7.7-18.6-28-28.5-47.4-23.2l-88 24C12.1 30.2 0 46 0 64c0 247.4 200.6 448 448 448 18 0 33.8-12.1 38.6-29.5l24-88c5.3-19.4-4.6-39.7-23.2-47.4l-96-40c-16.3-6.8-35.2-2.1-46.3 11.6L304.7 368c-70.4-33.3-127.4-90.3-160.7-160.7l49.3-40.3c13.7-11.2 18.4-30 11.6-46.3l-40-96z"
                />
              </svg>
              <span class="kpb-contact-list__phone-phone">{{ contact.phone }}</span>
            </template>
            <template v-if="contactDetailStyle === 'text'"
              ><span class="kpb-contact-list__phone-label">{{ $t('phone') }}:</span>
              <span class="kpb-contact-list__phone-phone">{{ contact.phone }}</span></template
            >
          </a>
          <a v-if="contact.email" class="kpb-contact-list__email" :href="`mailto:${contact.email}`">
            <template v-if="contactDetailStyle === 'icon'">
              <svg class="kpb-contact-list__contact-info-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path
                  d="M48 64C21.5 64 0 85.5 0 112c0 15.1 7.1 29.3 19.2 38.4l217.6 163.2c11.4 8.5 27 8.5 38.4 0l217.6-163.2c12.1-9.1 19.2-23.3 19.2-38.4 0-26.5-21.5-48-48-48H48zM0 176v208c0 35.3 28.7 64 64 64h384c35.3 0 64-28.7 64-64V176L294.4 339.2a63.9 63.9 0 0 1-76.8 0L0 176z"
                />
              </svg>
              <span class="kpb-contact-list__email-email">{{ showEmailLabel ? $t('sendEmail') : contact.email }}</span>
            </template>
            <template v-if="contactDetailStyle === 'text'"
              ><span class="kpb-contact-list__email-label">{{ $t('email') }}:</span>
              <span class="kpb-contact-list__email-email"> {{ contact.email }} </span></template
            >
          </a>
        </div>
        <img
          v-if="showCompanyLogo(contact)"
          height="64"
          class="kpb-contact-list__company-logo"
          :src="contact.company.logo"
          :alt="contact.company.name"
        />
      </div>
      <div class="kpb-contact-list__below">
        <slot name="below" />
      </div>
    </div>
  </Grid>
</template>

<script>
import Grid from './Grid.vue'

/** Component for rendering project contacts */
export default {
  props: {
    data: Object,
    variant: {
      type: String,
      default: 'vertical',
      validator: val => ['vertical', 'horizontal'].includes(val),
    },
    columns: {
      type: Number,
      default: 1,
    },
    showLogo: {
      type: Boolean,
      default: true,
    },
    showCompanyName: {
      type: Boolean,
      default: true,
    },
    showEmailLabel: {
      type: Boolean,
      default: false,
    },
    gap: String,
    aspectRatio: String,
    contactDetailStyle: {
      type: String,
      default: 'text',
      validator: val => ['text', 'icon'].includes(val),
    },
  },
  methods: {
    showCompany(contact) {
      if (!contact.company || !this.showCompanyName) return false
      return Boolean(contact.company.name)
    },
    showCompanyLogo(contact) {
      if (!contact.company || !this.showLogo) return false
      return Boolean(contact.company.logo)
    },
  },
  components: {
    Grid,
  },
}
</script>

<style lang="scss">
.kpb-contact-list {
  &__avatar {
    width: 12rem;
    aspect-ratio: var(--kpb-contact-list-aspect-ratio);

    object-fit: cover;
    object-position: center;

    flex-shrink: 0;
  }

  &__subtitle {
    color: rgba($dark, 0.6);
  }

  &__contact-info {
    display: flex;
    flex-direction: column;

    &-icon {
      width: 1em;
      opacity: 0.3;
    }
  }

  &__user-title {
    margin: 2px 0;
  }

  &__item {
    display: flex;
    gap: 1.5rem;
    border-radius: var(--kpb-border-radius, 0px);
  }

  &__name {
    font-weight: bold;
    font-size: 1.1em;
  }

  &__company {
    &-name {
      opacity: 0.5;
    }
    &-logo {
      margin-top: 1rem;
    }
  }

  &__email,
  &__phone {
    text-decoration: none;
    margin-right: 10px;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }

  /* Variants */
  &[data-kpb-variant='vertical'] {
    .kpb-contact-list__item {
      flex-direction: column;
    }

    .kpb-contact-list__name {
      margin: 0;
      font-size: 1.8em;
      font-weight: 400;
    }

    .kpb-contact-list__contact-info {
      margin-top: 1rem;
    }
  }
  &[data-kpb-variant='horizontal'] {
    .kpb-contact-list__item {
      flex-direction: row;
    }
  }
}
</style>
